/* eslint-disable class-methods-use-this */
/* eslint-disable no-underscore-dangle */
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { PortfolioResolutionStrategy } from './portfolio-resolution-strategy';

/**
 * Account Set-based portfolio resolution (new)
 * Uses Account Set membership to determine current/recommended status
 *
 * This strategy resolves portfolios based on their position within Account Sets.
 * A portfolio is "current" if it's in the `current` slot of any account set.
 * A portfolio is "recommended" if it's in the `recommendations` array of any account set.
 *
 * Key differences from CategoryBasedStrategy:
 * - Portfolios can be in multiple account sets
 * - Distinction between primary and secondary recommendations
 * - Position determined by array index, not by field value
 *
 * @example
 * const strategy = new AccountSetStrategy();
 * const currentPortfolios = strategy.getCurrentPortfolios(comparison);
 * // Returns portfolios at indices specified in accountSets[].current
 */
export class AccountSetStrategy implements PortfolioResolutionStrategy {
  getCurrentPortfolios(comparison: Comparison): ComparisonItem[] {
    if (!comparison.accountSets || comparison.accountSets.length === 0) {
      return [];
    }

    const currentIndices = comparison.accountSets.map(set => set.current).filter(idx => idx !== -1);

    return currentIndices.map(idx => comparison.items[idx]).filter(item => item !== undefined && item.type === COMPARISON_ITEM_TYPES.PORTFOLIO);
  }

  getRecommendedPortfolios(comparison: Comparison): ComparisonItem[] {
    if (!comparison.accountSets || comparison.accountSets.length === 0) {
      return [];
    }

    const recommendedIndices = comparison.accountSets.flatMap(set => set.recommendations.filter(rec => rec.itemId !== -1).map(rec => rec.itemId));

    return recommendedIndices.map(idx => comparison.items[idx]).filter(item => item !== undefined && item.type === COMPARISON_ITEM_TYPES.PORTFOLIO);
  }

  getPrimaryRecommendedPortfolios(comparison: Comparison): ComparisonItem[] {
    if (!comparison.accountSets || comparison.accountSets.length === 0) {
      return [];
    }

    const primaryIndices = comparison.accountSets.flatMap(set => set.recommendations.filter(rec => rec.isPrimary && rec.itemId !== -1).map(rec => rec.itemId));

    return primaryIndices.map(idx => comparison.items[idx]).filter(item => item !== undefined && item.type === COMPARISON_ITEM_TYPES.PORTFOLIO);
  }

  isCurrentPortfolio(comparison: Comparison, item: ComparisonItem): boolean {
    if (!comparison.accountSets || comparison.accountSets.length === 0) {
      return false;
    }

    const itemIndex = comparison.items.findIndex(i => i._id === item._id);
    if (itemIndex < 0) return false;

    return comparison.accountSets.some(set => set.current === itemIndex);
  }

  isRecommendedPortfolio(comparison: Comparison, item: ComparisonItem): boolean {
    if (!comparison.accountSets || comparison.accountSets.length === 0) {
      return false;
    }

    const itemIndex = comparison.items.findIndex(i => i._id === item._id);
    if (itemIndex < 0) return false;

    return comparison.accountSets.some(set => set.recommendations.some(rec => rec.itemId === itemIndex));
  }

  areAllPortfoliosAssigned(comparison: Comparison): boolean {
    if (!comparison.accountSets || comparison.accountSets.length === 0) {
      return false;
    }

    const portfolios = comparison.items.filter(item => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO);

    return portfolios.every((portfolio, index) => {
      const isInAccountSet = comparison.accountSets!.some(set => set.current === index || set.recommendations.some(rec => rec.itemId === index));
      return isInAccountSet;
    });
  }
}
